import { useState, useMemo, useEffect } from "react";
import { isChatbotTypeLocked } from "./create-chatbot.entitlements";
import { useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Bot, Globe, FileText, MessageSquare, ArrowLeft, Palette, Zap, Layers, Send, User, LifeBuoy, ShieldCheck, AlertTriangle, Lock, CheckCircle2, type LucideIcon } from "lucide-react";
import type { Chatbot } from "@shared/schema";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Link } from "wouter";
import { BOT_THEMES, getThemeById } from "@/lib/themes";

// Each card on the "choose a bot type" step. The optional `preset*` fields
// turn a card into a curated template (e.g. IT Helpdesk) that:
//   - maps to a different `underlyingType` when posting to the server
//   - sends a `presetKey` so the server enables the right tools
//   - pre-fills the system prompt + welcome message in step 2
// Anything without those fields is a plain bot type.
interface BotTypeOption {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  color: string;
  badge: string;
  presetKey?: string;
  underlyingType?: string;
  presetSystemPrompt?: string;
  presetWelcomeMessage?: string;
}

const botTypes: BotTypeOption[] = [
  {
    id: "website",
    title: "Website Chatbot",
    description: "Crawls one or multiple websites and responds based on website content. Ideal for customer support and FAQ automation.",
    icon: Globe,
    color: "#0c3547",
    badge: "Website",
  },
  {
    id: "document",
    title: "Document Chatbot",
    description: "Upload documents (PDF, TXT, CSV) and the bot answers based on their content. Supports forms and document-specific queries.",
    icon: FileText,
    color: "#1a365d",
    badge: "Documents",
  },
  {
    id: "combo",
    title: "Combo Chatbot",
    description: "Combines website content AND uploaded documents for comprehensive coverage. Great for businesses with both web presence and document resources including forms.",
    icon: Layers,
    color: "#7c3aed",
    badge: "Website + Documents",
  },
  {
    id: "ai-enhanced",
    title: "AI-Enhanced Chatbot",
    description: "The most powerful option — responds to any query using OpenAI's full capabilities. Optionally add website or document knowledge for grounded answers, or use it as an open-ended AI assistant without any restrictions.",
    icon: Zap,
    color: "#0ea5e9",
    badge: "OpenAI Powered",
  },
  {
    id: "it-helpdesk",
    title: "IT Helpdesk (ServiceNow)",
    description: "Pre-configured to open and manage ServiceNow incidents, look up ticket status, search the IT knowledge base, and assist employees with IT requests. Connect ServiceNow under Settings → Integrations first.",
    icon: LifeBuoy,
    color: "#1FA8E0",
    badge: "ServiceNow",
    presetKey: "it-helpdesk",
    underlyingType: "ai-enhanced",
    presetSystemPrompt:
      "You are the company IT Helpdesk assistant. Help employees triage and resolve IT issues quickly. " +
      "Always check the ServiceNow knowledge base (search_kb) before opening a ticket. If a ticket is needed, " +
      "use create_incident with a clear short description and an appropriate priority (urgent only for outages " +
      "or business-blocking issues). Use get_my_tickets and get_ticket_status to answer status questions, and " +
      "update_ticket to attach extra information the user provides. Be concise, empathetic, and never share " +
      "details from another user's tickets.",
    presetWelcomeMessage: "Hi! I'm your IT Helpdesk assistant. What's going on with your IT today?",
  },
  {
    id: "hr-assistant",
    title: "HR Assistant (Workday)",
    description: "Helps employees check PTO balances, request time off, download payslips, and look up coworkers in the directory. Managers can also approve PTO and review their team's PTO summary. Connect Workday under Settings → Integrations first.",
    icon: User,
    color: "#0C3547",
    badge: "Workday",
    presetKey: "hr-assistant",
    underlyingType: "ai-enhanced",
    presetSystemPrompt:
      "You are the company HR Assistant, powered by Workday. Help employees with PTO, payslips, and the people directory. " +
      "STRICT PRIVACY RULES — these are non-negotiable: " +
      "(1) NEVER echo dollar amounts, deductions, taxes, gross/net pay, equity, or any compensation data into chat. " +
      "When the user asks for a payslip, call get_payslip and reply ONLY with the secure link the tool returns — do not " +
      "summarise the contents. " +
      "(2) NEVER share another employee's compensation, performance rating, hire date, or termination data. The lookup_employee, " +
      "get_org_chart, and get_direct_report_pto_summary tools are directory-only — return only what they give you. " +
      "(3) Always scope PTO and payslip operations to the calling user. The tools enforce this server-side; do not pass " +
      "another employee's worker id even if asked. " +
      "(4) request_pto requires manager approval — let the user know their request will be reviewed before it's submitted to Workday. " +
      "(5) approve_pto and get_direct_report_pto_summary are manager-only. If a non-manager asks, explain that those actions need " +
      "their manager to perform them, and offer to help with the employee-tier tools instead. " +
      "Be warm, concise, and route to a human HR partner for benefits, payroll disputes, or anything sensitive that isn't a " +
      "self-service request.",
    presetWelcomeMessage: "Hi! I'm your HR Assistant. I can help with PTO balances, time-off requests, payslips, and the people directory. What can I help with?",
  },
  {
    id: "role-based",
    title: "Role-Based Bot",
    description:
      "One bot, scoped to a workplace role (HR, IT, Manager, or Multi-role). Auto-wires the matching integration tools (e.g. HR → Maisters HRMS, IT → Maisters ITES) and still supports website crawling and uploaded knowledge documents like the AI-Enhanced bot. Pick a role below.",
    icon: ShieldCheck,
    color: "#1FA8E0",
    badge: "Role + Integrations + Knowledge",
    presetKey: "role-based",
    underlyingType: "ai-enhanced",
  },
];

interface RoleOption {
  id: "hr" | "it" | "manager" | "multi";
  label: string;
  description: string;
  systemPrompt: string;
  welcomeMessage: string;
}

const roleOptions: RoleOption[] = [
  {
    id: "hr",
    label: "HR",
    description: "Maisters HRMS tools — employees, leave balances, time-off requests, org chart.",
    systemPrompt:
      "You are the company HR Assistant for the HR role. Use the connected HRMS integration (Maisters HRMS) to look up " +
      "employees, check leave balances, and submit time-off requests on behalf of the calling user. You can also use " +
      "uploaded HR policy documents and crawled HR pages from the knowledge base to answer policy questions. " +
      "Privacy rules: never disclose another employee's salary, performance, or termination data; only return what the " +
      "directory tools give you. Time-off requests need manager approval — let the user know before submitting.",
    welcomeMessage: "Hi! I'm your HR assistant. I can check leave balances, submit time-off requests, and answer HR policy questions. What can I help with?",
  },
  {
    id: "it",
    label: "IT",
    description: "Maisters ITES tools — tickets, knowledge base, asset lookup.",
    systemPrompt:
      "You are the company IT Helpdesk assistant for the IT role. Use the connected ITES integration (Maisters ITES) to " +
      "search the knowledge base before opening anything, look up existing tickets, create new tickets when needed, and " +
      "check assigned assets. Combine these with any uploaded IT runbooks or crawled internal pages in the knowledge base. " +
      "Always search the knowledge base first; only create a ticket if the user explicitly asks or the issue clearly needs " +
      "human IT involvement. New tickets require approval before they're filed.",
    welcomeMessage: "Hi! I'm your IT helpdesk assistant. I can search the knowledge base, check ticket status, and open new tickets. What's going on?",
  },
  {
    id: "manager",
    label: "Manager",
    description: "Both HR and IT toolsets, plus manager-tier actions (HRMS approvals + ITES ticket triage).",
    systemPrompt:
      "You are an assistant for people managers. You have access to both HR (Maisters HRMS) and IT (Maisters ITES) " +
      "integrations and can answer questions about your direct reports' leave balances, review time-off requests, look up " +
      "team tickets, and search internal knowledge documents. Always confirm sensitive actions before performing them and " +
      "scope queries to the requesting manager's own team.",
    welcomeMessage: "Hi! I'm your manager assistant. I can help with team time-off, IT tickets, and policy questions. What do you need?",
  },
  {
    id: "multi",
    label: "Multi-role",
    description: "Everything: HR + IT integrations together. Best for a single all-purpose internal helper.",
    systemPrompt:
      "You are the company's all-in-one internal assistant. You have access to both HR (Maisters HRMS) and IT (Maisters " +
      "ITES) integrations, plus website-crawled internal content and uploaded knowledge documents. Route the user to the " +
      "right tools based on what they ask: HR-style requests → HRMS tools, IT-style requests → ITES tools, policy or how-to " +
      "questions → knowledge base. Be concise and never expose data from one user about another.",
    welcomeMessage: "Hi! I'm your all-in-one internal assistant. I can help with HR, IT, and anything in the knowledge base. What can I do for you?",
  },
];

interface ProviderModels {
  provider: string;
  providerName: string;
  models: string[];
}

const iconOptions = [
  { value: "bot", label: "Robot", icon: Bot },
  { value: "message", label: "Message", icon: MessageSquare },
  { value: "globe", label: "Globe", icon: Globe },
  { value: "zap", label: "Lightning", icon: Zap },
  { value: "file", label: "Document", icon: FileText },
  { value: "user", label: "User", icon: User },
];

export default function CreateChatbot() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user, organization } = useAuth();

  // Pull the live bot list to compute the org's enabled-bot usage. We do
  // this in the create form so the admin sees BEFORE they hit submit that
  // the new bot will be saved as inactive (because the plan is full),
  // rather than learning after the fact via a toast.
  const { data: existingBots } = useQuery<Chatbot[]>({ queryKey: ["/api/chatbots"] });

  // Plan entitlements from the server — single source of truth for the
  // bot cap and which bot types this org's plan unlocks. The backend
  // enforces both regardless; this just keeps the UI honest up front.
  const { data: entitlements } = useQuery<{
    plan: string;
    planName: string;
    botLimit: number;
    activeBots: number;
    botSlotsRemaining: number;
    allBotTypesAllowed: boolean;
    allowedBotTypes: string[];
  }>({ queryKey: ["/api/organization/entitlements"] });

  const activeBotCount = entitlements?.activeBots
    ?? (existingBots?.filter((b) => (b.status ?? "active") === "active").length ?? 0);
  const botLimit = entitlements?.botLimit ?? organization?.botLimit ?? null;
  const wouldBeDisabled = !!botLimit && activeBotCount >= botLimit;

  const isTypeLocked = (type: BotTypeOption) => isChatbotTypeLocked(type, entitlements);

  // AI keys are per-chatbot now (added on the bot's Integrations tab
  // *after* it exists). The wizard therefore can't know which provider
  // the new bot will use, so we fall back to the catalog's curated model
  // list and let admins switch the model from the chatbot's Configure
  // tab once a key is saved.
  const { data: providerCatalog } = useQuery<Array<{ id: string; name: string; fallbackModels: string[] }>>({
    queryKey: ["/api/ai-providers"],
  });

  const models = useMemo(() => {
    if (!providerCatalog) return [];
    return providerCatalog.flatMap(p =>
      p.fallbackModels.map(m => ({
        value: m,
        label: m,
        provider: p.name,
      }))
    );
  }, [providerCatalog]);

  // The Configure tab guards live chat once the bot exists; here we just
  // need a non-empty model dropdown so the wizard can submit.
  const hasApiKey = true;

  useEffect(() => {
    if (models.length > 0 && !model) {
      setModel(models[0].value);
    }
  }, [models]);

  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState("");
  const [selectedRole, setSelectedRole] = useState<RoleOption["id"] | "">("");
  const [name, setName] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [welcomeMessage, setWelcomeMessage] = useState("Hello! How can I help you today?");
  const [endMessage, setEndMessage] = useState("Thank you for chatting with us! Please rate your experience.");
  const [scopeDescription, setScopeDescription] = useState("");
  const [model, setModel] = useState("");
  const [temperature, setTemperature] = useState(7);
  const [maxTokens, setMaxTokens] = useState(2048);
  const [primaryColor, setPrimaryColor] = useState("#0c3547");
  const [selectedTheme, setSelectedTheme] = useState("professional-blue");
  const [botIcon, setBotIcon] = useState("bot");
  const [allowedDomains, setAllowedDomains] = useState("");

  const currentTheme = getThemeById(selectedTheme);

  const createMutation = useMutation({
    mutationFn: async () => {
      const domains = allowedDomains.split("\n").map(d => d.trim()).filter(Boolean);
      const preset = botTypes.find(t => t.id === selectedType);
      // Preset templates (e.g. "it-helpdesk") map to a real chatbot `type`
      // for storage/runtime, plus a server-side hint that wires the
      // ServiceNow tools onto the new bot.
      const realType = preset?.underlyingType ?? selectedType;
      // For Role-Based Bots, append the chosen role so the server can
      // wire the right tool bundle (HR, IT, Manager, Multi-role).
      const presetKey =
        preset?.presetKey === "role-based" && selectedRole
          ? `role-based:${selectedRole}`
          : preset?.presetKey;
      const res = await apiRequest("POST", "/api/chatbots", {
        name,
        type: realType,
        systemPrompt,
        welcomeMessage,
        endMessage,
        scopeDescription,
        model,
        temperature,
        maxTokens,
        primaryColor,
        theme: selectedTheme,
        botIcon,
        allowedDomains: domains.length > 0 ? domains : null,
        status: "active",
        ...(presetKey ? { presetKey } : {}),
      });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots"] });
      toast({ title: "Chatbot created!", description: `${name} is ready to configure.` });
      setLocation(`/chatbots/${data.id}`);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link href="/chatbots">
          <Button variant="ghost" size="icon" aria-label="Back to chatbots" data-testid="button-back">
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-create-title">Create Chatbot</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Step {step} of 3 {step === 1 ? "— Choose bot type" : step === 2 ? "— Configure behavior" : "— Design & appearance"}
          </p>
        </div>
      </div>

      <div className="flex gap-1 mb-4">
        {[1, 2, 3].map(s => (
          <div key={s} className={`h-1 flex-1 rounded-full ${s <= step ? "bg-primary" : "bg-muted"}`} />
        ))}
      </div>

      {wouldBeDisabled && (
        <div
          className="flex items-start gap-3 rounded-lg border border-amber-300/50 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700/40 p-3"
          data-testid="banner-create-disabled-warning"
        >
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-amber-900 dark:text-amber-100">
              You're at your plan's enabled-bot limit ({botLimit}).
            </p>
            <p className="text-amber-800/90 dark:text-amber-200/80 mt-0.5">
              You can finish creating this bot, but it will be saved as <strong>disabled</strong>. To enable it later,
              disable another bot or upgrade your plan.
            </p>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="grid gap-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="text-lg font-semibold">What type of chatbot do you need?</h2>
            {botLimit != null && (
              <Badge variant="outline" className="text-xs" data-testid="badge-bot-usage">
                {activeBotCount} of {botLimit} chatbots used{entitlements?.planName ? ` · ${entitlements.planName} plan` : ""}
              </Badge>
            )}
          </div>
          <div role="radiogroup" aria-label="Chatbot type" className="grid gap-4">
          {botTypes.map((type) => {
            const locked = isTypeLocked(type);
            const isSelected = selectedType === type.id;
            const selectType = () => {
              if (locked) {
                toast({
                  title: "Upgrade required",
                  description: `${type.title} is not included in your ${entitlements?.planName ?? "current"} plan. Upgrade your subscription to access this chatbot type.`,
                  variant: "destructive",
                });
                return;
              }
              setSelectedType(type.id);
              // Preset templates pre-fill the system prompt + welcome
              // line so admins can land on Step 2 with the bot already
              // shaped correctly. They can still edit before saving.
              if (type.presetSystemPrompt && !systemPrompt) setSystemPrompt(type.presetSystemPrompt);
              if (type.presetWelcomeMessage) setWelcomeMessage(type.presetWelcomeMessage);
            };
            return (
            <Card
              key={type.id}
              role="radio"
              aria-checked={isSelected}
              aria-disabled={locked || undefined}
              tabIndex={locked ? -1 : 0}
              className={`relative transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${locked ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:shadow-md"} ${isSelected ? "border-primary border-2 bg-primary/5 shadow-md" : "border"}`}
              onClick={selectType}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  selectType();
                }
              }}
              data-testid={`card-type-${type.id}`}
            >
              {isSelected && (
                <CheckCircle2
                  className="absolute top-3 right-3 w-5 h-5 text-primary"
                  aria-hidden="true"
                  data-testid={`icon-selected-${type.id}`}
                />
              )}
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div
                    className="w-12 h-12 rounded-md flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: type.color }}
                  >
                    <type.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{type.title}</h3>
                      <Badge variant="outline" className="text-[10px]">{type.badge}</Badge>
                      {locked && (
                        <Badge variant="secondary" className="text-[10px] gap-1" data-testid={`badge-locked-${type.id}`}>
                          <Lock className="w-3 h-3" /> Upgrade to unlock
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{type.description}</p>
                  </div>
                </div>
                {type.id === "role-based" && selectedType === "role-based" && (
                  // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                  <div
                    className="mt-4 pt-4 border-t space-y-3"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="section-role-picker"
                  >
                    <div className="text-sm font-medium">Pick this bot's role</div>
                    <RadioGroup
                      value={selectedRole}
                      onValueChange={(val) => {
                        const role = roleOptions.find((r) => r.id === val);
                        if (!role) return;
                        setSelectedRole(role.id);
                        // Always re-apply the role's prompt + welcome on a
                        // role switch so the bot stays in sync with the
                        // chosen integration bundle. Admin can still edit
                        // these on step 2.
                        setSystemPrompt(role.systemPrompt);
                        setWelcomeMessage(role.welcomeMessage);
                      }}
                      className="grid gap-2"
                    >
                      {roleOptions.map((role) => (
                        <Label
                          key={role.id}
                          htmlFor={`role-${role.id}`}
                          className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer hover-elevate active-elevate-2 ${
                            selectedRole === role.id ? "border-primary" : ""
                          }`}
                          data-testid={`label-role-${role.id}`}
                        >
                          <RadioGroupItem
                            id={`role-${role.id}`}
                            value={role.id}
                            data-testid={`radio-role-${role.id}`}
                          />
                          <div className="flex-1">
                            <div className="text-sm font-medium">{role.label}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">{role.description}</div>
                          </div>
                        </Label>
                      ))}
                    </RadioGroup>
                    <p className="text-xs text-muted-foreground">
                      Tip: connect "Maisters HRMS" / "Maisters ITES" under Settings → Integrations first (one-click "Add sample integrations" works). Crawler and uploaded knowledge documents can still be added on the bot detail page after creation.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
            );
          })}
          </div>
          <Button
            onClick={() => setStep(2)}
            disabled={!selectedType || (selectedType === "role-based" && !selectedRole)}
            className="mt-2"
            data-testid="button-next-step"
          >
            Continue
          </Button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Basic Information</CardTitle>
              <CardDescription>Give your chatbot a name and personality</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Chatbot Name</Label>
                <Input id="name" placeholder="e.g., Customer Support Bot" value={name} onChange={(e) => setName(e.target.value)} data-testid="input-bot-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="scope">Scope Description</Label>
                <Input id="scope" placeholder="e.g., E-commerce support for shoes.example.com" value={scopeDescription} onChange={(e) => setScopeDescription(e.target.value)} data-testid="input-scope" />
                <p className="text-xs text-muted-foreground">Describe what this bot covers. The bot will only answer questions within this scope.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="system-prompt">System Prompt</Label>
                <Textarea id="system-prompt" placeholder="You are a helpful assistant that..." value={systemPrompt} onChange={(e) => setSystemPrompt(e.target.value)} className="min-h-[100px] resize-none" data-testid="textarea-system-prompt" />
                <p className="text-xs text-muted-foreground">Define how your chatbot should behave and respond</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="welcome">Welcome Message</Label>
                  <Input id="welcome" placeholder="Hello! How can I help you today?" value={welcomeMessage} onChange={(e) => setWelcomeMessage(e.target.value)} data-testid="input-welcome-message" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end-msg">End of Chat Message</Label>
                  <Input id="end-msg" placeholder="Thank you for chatting with us!" value={endMessage} onChange={(e) => setEndMessage(e.target.value)} data-testid="input-end-message" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Model Configuration</CardTitle>
              <CardDescription>Choose the AI model and tune its behavior</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="select-model">AI Model</Label>
                {models.length > 0 ? (
                  <Select value={model} onValueChange={setModel}>
                    <SelectTrigger id="select-model" data-testid="select-model"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {providerCatalog?.map((p) => (
                        <div key={p.id}>
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">{p.name}</div>
                          {p.fallbackModels.map((m) => (
                            <SelectItem key={`${p.id}:${m}`} value={m}>{m}</SelectItem>
                          ))}
                        </div>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="p-3 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30">
                    <p className="text-sm text-amber-800 dark:text-amber-200" data-testid="text-no-models">
                      Loading available models…
                    </p>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Temperature: {temperature / 10}</Label>
                <Slider value={[temperature]} onValueChange={([v]) => setTemperature(v)} min={0} max={10} step={1} aria-label="Temperature" data-testid="slider-temperature" />
                <p className="text-xs text-muted-foreground">Lower = more focused, Higher = more creative</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="max-tokens">Max Tokens</Label>
                <Input id="max-tokens" type="number" value={maxTokens} onChange={(e) => setMaxTokens(parseInt(e.target.value) || 2048)} data-testid="input-max-tokens" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Domain Restrictions</CardTitle>
              <CardDescription>Specify which domains are allowed to use this chatbot</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Textarea placeholder={"example.com\napp.example.com\nshop.example.com"} value={allowedDomains} onChange={(e) => setAllowedDomains(e.target.value)} className="min-h-[80px] resize-none font-mono text-sm" data-testid="textarea-domains" />
              <p className="text-xs text-muted-foreground">One domain per line. Leave empty to allow all domains. The embed widget will validate the domain before loading.</p>
            </CardContent>
          </Card>

          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => setStep(1)} data-testid="button-go-back">Back</Button>
            <Button onClick={() => setStep(3)} disabled={!name} data-testid="button-next-step-2">Continue to Design</Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><Palette className="w-5 h-5" />Theme</CardTitle>
                <CardDescription>Choose a visual theme for your chatbot</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {BOT_THEMES.map((theme) => (
                    <button
                      key={theme.id}
                      type="button"
                      onClick={() => { setSelectedTheme(theme.id); setPrimaryColor(theme.primaryColor); }}
                      className={`p-3 rounded-md border text-left transition-all ${selectedTheme === theme.id ? "ring-2 ring-primary border-primary" : "hover:border-foreground/30"}`}
                      data-testid={`button-theme-${theme.id}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-5 h-5 rounded-full" style={{ backgroundColor: theme.primaryColor }} />
                        <span className="text-xs font-medium truncate">{theme.name}</span>
                      </div>
                      <div className="flex gap-1">
                        <div className="w-full h-2 rounded-full" style={{ backgroundColor: theme.headerBg }} />
                      </div>
                      <div className="flex gap-1 mt-1">
                        <div className="flex-1 h-2 rounded-sm" style={{ backgroundColor: theme.userBubble }} />
                        <div className="flex-1 h-2 rounded-sm" style={{ backgroundColor: theme.botBubble }} />
                      </div>
                    </button>
                  ))}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="input-primary-color">Primary Color Override</Label>
                  <div className="flex items-center gap-3">
                    <input id="input-primary-color" type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="w-10 h-10 rounded-md border cursor-pointer" data-testid="input-primary-color" />
                    <Input value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="max-w-[140px]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Bot Icon</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {iconOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setBotIcon(opt.value)}
                      className={`p-3 rounded-md border flex flex-col items-center gap-1 w-16 transition-all ${botIcon === opt.value ? "ring-2 ring-primary border-primary" : "hover:border-foreground/30"}`}
                      data-testid={`button-icon-${opt.value}`}
                    >
                      <opt.icon className="w-5 h-5" />
                      <span className="text-[9px] text-muted-foreground">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => setStep(2)} data-testid="button-go-back-2">Back</Button>
              <Button onClick={() => createMutation.mutate()} disabled={!name || createMutation.isPending} data-testid="button-create-bot">
                {createMutation.isPending ? "Creating..." : "Create Chatbot"}
              </Button>
            </div>
          </div>

          <div className="sticky top-6">
            <p className="text-sm font-medium mb-3 text-muted-foreground">Live Preview</p>
            <div className="rounded-xl overflow-hidden shadow-lg border" style={{ borderRadius: currentTheme.borderRadius, fontFamily: currentTheme.fontFamily }}>
              <div className="px-4 py-3 flex items-center gap-3" style={{ backgroundColor: currentTheme.headerBg, color: currentTheme.headerText }}>
                <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: primaryColor || currentTheme.primaryColor }}>
                  {(() => {
                    const IconComp = iconOptions.find(o => o.value === botIcon)?.icon || Bot;
                    return <IconComp className="w-4 h-4 text-white" />;
                  })()}
                </div>
                <span className="text-sm font-semibold">{name || "Your Bot"}</span>
              </div>
              <div className="p-4 space-y-3" style={{ backgroundColor: currentTheme.backgroundColor, minHeight: "280px" }}>
                <div className="flex gap-2">
                  <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: currentTheme.primaryColor }}>
                    <Bot className="w-3 h-3 text-white" />
                  </div>
                  <div className="px-3 py-2 rounded-lg max-w-[80%] text-sm" style={{ backgroundColor: currentTheme.botBubble, color: currentTheme.botBubbleText, borderRadius: currentTheme.borderRadius }}>
                    {welcomeMessage || "Hello! How can I help you today?"}
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <div className="px-3 py-2 rounded-lg max-w-[80%] text-sm" style={{ backgroundColor: currentTheme.userBubble, color: currentTheme.userBubbleText, borderRadius: currentTheme.borderRadius }}>
                    Tell me about your services
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: currentTheme.primaryColor }}>
                    <Bot className="w-3 h-3 text-white" />
                  </div>
                  <div className="px-3 py-2 rounded-lg max-w-[80%] text-sm" style={{ backgroundColor: currentTheme.botBubble, color: currentTheme.botBubbleText, borderRadius: currentTheme.borderRadius }}>
                    I'd be happy to help you learn about our services!
                  </div>
                </div>
              </div>
              <div className="p-3 border-t flex gap-2" style={{ backgroundColor: currentTheme.backgroundColor }}>
                <div className="flex-1 px-3 py-1.5 rounded-md text-xs border" style={{ borderColor: currentTheme.primaryColor + "40" }}>
                  Type a message...
                </div>
                <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: currentTheme.primaryColor }}>
                  <Send className="w-3.5 h-3.5 text-white" />
                </div>
              </div>
            </div>
            <div className="mt-3 p-3 rounded-md bg-muted/50">
              <p className="text-xs text-muted-foreground">
                <strong>Type:</strong> {botTypes.find(t => t.id === selectedType)?.title}
                {scopeDescription && <><br /><strong>Scope:</strong> {scopeDescription}</>}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
